// The portal session: what the app knows about the signed-in external user.
//
// sessionStorage, per browser tab, the same isolation the staff apps use -- two tabs can be signed in
// to two different accounts without either clobbering the other.
//
// Deliberately small. An external contact has no positions, user groups or work groups, so there is
// no active-visibility concept here and nothing to pick after sign-in beyond the account itself.

export type PortalAccount = {
    AccountUSID: string;
    AccountName: string;
    MembershipUSID?: string;
};

export type PortalUser = {
    USID: string;
    Name: string;
    Email: string;
    CompanyName?: string | null;
};

const ACCESS_TOKEN = "SupplierPortal.AccessToken";
const REFRESH_TOKEN = "SupplierPortal.RefreshToken";
const IDENTITY_TOKEN = "SupplierPortal.IdentityToken";
const SESSION_USID = "SupplierPortal.Session";
const ACCOUNT = "SupplierPortal.Account";
const USER = "SupplierPortal.User";

function read<T>(key: string): T | null {
    try {
        const raw = sessionStorage.getItem(key);
        return raw ? (JSON.parse(raw) as T) : null;
    } catch {
        return null;
    }
}

export function establishSession(payload: any) {
    sessionStorage.setItem(ACCESS_TOKEN, payload.AccessToken ?? "");
    sessionStorage.setItem(REFRESH_TOKEN, payload.RefreshToken ?? "");
    sessionStorage.setItem(IDENTITY_TOKEN, payload.IdentityToken ?? "");
    sessionStorage.setItem(SESSION_USID, payload.Session ?? "");
    sessionStorage.setItem(ACCOUNT, JSON.stringify(payload.Account ?? null));
    sessionStorage.setItem(USER, JSON.stringify(payload.ExternalUser ?? null));
}

export function clearSession() {
    [ACCESS_TOKEN, REFRESH_TOKEN, IDENTITY_TOKEN, SESSION_USID, ACCOUNT, USER]
        .forEach(key => sessionStorage.removeItem(key));
}

export function getAccessToken(): string | null {
    return sessionStorage.getItem(ACCESS_TOKEN);
}

export function getAccount(): PortalAccount | null {
    return read<PortalAccount>(ACCOUNT);
}

export function getUser(): PortalUser | null {
    return read<PortalUser>(USER);
}

export function isSignedIn(): boolean {
    return !!getAccessToken() && !!getAccount();
}
